import styled from '@emotion/styled';
import React, { FC, useState } from 'react';
import { useCart } from '~Components/CartProvider';
import TypeChanger, { CourseType } from '~ComponentsImproved/TSR/TypeChanger';
import { VenueData_venue_company_courseTypes } from '../__queries__';
import { useButton, useI18n } from '~Hooks';
import { Button } from '~Styles/buttons';
import Heading from '~ComponentsImproved/Typography/Heading';

const Message = styled.div`
  color: var(--colorWhite);
  border-radius: 4px;
  padding: 6px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--colorBlueGray1200);
`;

const Type = styled.div`
  background: ${(props) => props.color};
  padding: 4px;
  text-align: center;
  height: 24px;
  min-width: 24px;
  color: var(--colorBlueGray1200);
  line-height: 16px;
  border-radius: 24px;
  font-weight: 600;
  font-size: 10px;
  box-sizing: border-box;
  aspect-ratio: 1;
`;

const Title = styled.h5`
  border-bottom: 1px solid #41474f;
  padding: 16px 16px 10px;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  color: var(--colorBlueGray600);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RemoveButton = styled(Button)`
  background: transparent;
  color: var(--colorWhite);
  border: 1px solid #666b71;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
`;

interface Props {
  allCourseTypes: VenueData_venue_company_courseTypes[];
}

interface TicketMessageProps {
  id: string;
  message: string;
  courseType?: VenueData_venue_company_courseTypes;
}

const TicketMessage: FC<TicketMessageProps> = ({ id, message, courseType }) => {
  const cart = useCart();
  const [showChangeCTModal, setChangeCTModal] = useState(false);

  const changeCourseType = (ct: CourseType) => {
    setChangeCTModal(false);

    cart.removeMessagesFromTicket([id]);
    cart.addMessageToTicket(id, message, ct.id);
  };

  return (
    <div key={id}>
      <Message>
        <Heading level={5} color="white">
          {message}
        </Heading>

        {courseType && (
          <Type
            // If the id includes "wait" the message comes from the Wait course type functionality and should not be clickable
            onTouchStart={() => (id.includes('wait') ? undefined : setChangeCTModal(true))}
            color={courseType.color}>
            {courseType.label}
          </Type>
        )}
      </Message>

      {showChangeCTModal && (
        <TypeChanger onClose={() => setChangeCTModal(false)} onChange={changeCourseType} />
      )}
    </div>
  );
};

const TicketMessageRows: FC<Props> = ({ allCourseTypes }) => {
  const cart = useCart();
  const { i18n } = useI18n();

  const removeButton = useButton({
    onClick: () => {
      cart.removeMessagesFromTicket();
    },
  });

  if (cart.ticketMessages.length === 0) {
    return null;
  }

  return (
    <div>
      <Title>
        <span>{i18n('POS.Messages')}</span>
        <RemoveButton {...removeButton} size="small">
          {i18n('POS.Remove')}
        </RemoveButton>
      </Title>
      {cart.ticketMessages.map((tm, index) => {
        const foundCourseType = allCourseTypes.find((ct) => ct.id === tm.courseId);
        return (
          <TicketMessage
            key={index}
            id={tm.id}
            message={tm.message}
            courseType={foundCourseType}
          />
        );
      })}
    </div>
  );
};

export default TicketMessageRows;
